import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Box,
  Fab,
  TextField,
  Button,
  Paper,
  Typography,
  IconButton,
  Divider,
  Avatar,
} from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';

const TypingDots = () => (
  <Box sx={{ display: 'inline-flex', gap: 0.5, alignItems: 'center', height: 24 }}>
    {[0, 1, 2].map(i => (
      <Box
        key={i}
        sx={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          bgcolor: 'white',
          animation: `typing-dot 1.4s infinite`,
          animationDelay: `${i * 0.2}s`,
        }}
      />
    ))}
    <style>{`
      @keyframes typing-dot {
        0%, 80%, 100% { opacity: 0.3; transform: translateY(0); }
        40% { opacity: 1; transform: translateY(-6px); }
      }
    `}</style>
  </Box>
);

const ChatWidget = ({
  apiEndpoint,
  title = 'Chatbot',
  user,
  allCourses,
  viewedChaptersMap,
  chapterCounts
}) => {
  const [question, setQuestion] = useState('');
  const [chat, setChat] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const chatBoxRef = useRef(null);
  const minTypingTime = 3000;

  const sendMessage = async () => {
    if (!question.trim() || loading) return;

    const userMsg = { sender: 'user', text: question };
    setChat(prev => [...prev, userMsg]);
    setQuestion('');
    setLoading(true);

    const typingMsg = { sender: 'bot', text: 'typing' };
    setChat(prev => [...prev, typingMsg]);

    const startTime = Date.now();

    try {
      // ✅ Send 'message' to match backend
      const res = await axios.post(apiEndpoint, { message: question }, { timeout: 10000 });

      const elapsed = Date.now() - startTime;
      const waitTime = elapsed < minTypingTime ? minTypingTime - elapsed : 0;
      await new Promise(resolve => setTimeout(resolve, waitTime));

      const botMsg = {
        sender: 'bot',
        text: res?.data?.response || '🤖 No response from the server.',
      };

      setChat(prev => {
        const newChat = prev.slice(0, -1);
        return [...newChat, botMsg];
      });
    } catch (err) {
      const elapsed = Date.now() - startTime;
      const waitTime = elapsed < minTypingTime ? minTypingTime - elapsed : 0;
      await new Promise(resolve => setTimeout(resolve, waitTime));

      setChat(prev => {
        const newChat = prev.slice(0, -1);
        return [
          ...newChat,
          { sender: 'bot', text: '⚠️ Unable to contact chatbot server. Please try again later.' },
        ];
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
    }
  }, [chat]);

  useEffect(() => {
    const hasData = user && allCourses?.length && Object.keys(viewedChaptersMap || {}).length && Object.keys(chapterCounts || {}).length;
    if (!hasData) return;

    const message = generateWelcomeMessage(user, allCourses, viewedChaptersMap, chapterCounts);

    const showWelcomeMessage = async () => {
      setIsOpen(true);
      setLoading(true);
      setChat([{ sender: 'bot', text: 'typing' }]);

      await new Promise(resolve => setTimeout(resolve, 3000));
      setChat([{ sender: 'bot', text: message }]);
      setLoading(false);

      setTimeout(() => setIsOpen(false), 5000);
    };

    const delay = Math.floor(Math.random() * (8000 - 2000 + 1)) + 2000;
    setTimeout(showWelcomeMessage, delay);
  }, [user, allCourses, viewedChaptersMap, chapterCounts]);

  const generateWelcomeMessage = (user, allCourses, viewedMap, countsMap) => {
    const name = user.username?.split(" ")[0] || "there";
    const enrolledCourses = user.enrolledCourses || [];
    let message = `👋 Hey ${name}, welcome back!\n\n`;

    if (!enrolledCourses.length) {
      const suggestion = allCourses[0];
      message += `🎓 You haven’t enrolled in any courses yet.\nTry starting with **${suggestion.title}**. To join, type: \`enroll(${suggestion.id})\` 🚀`;
      return message;
    }

    message += `📘 You're enrolled in ${enrolledCourses.length} course${enrolledCourses.length > 1 ? 's' : ''}:\n`;
    enrolledCourses.forEach(course => {
      const viewed = viewedMap[course.id]?.length || 0;
      const total = countsMap[course.id] || 0;
      const percent = total ? Math.round((viewed / total) * 100) : 0;
      const emoji = percent === 100 ? "🏁" : percent >= 75 ? "✅" : percent >= 50 ? "📈" : percent > 0 ? "🕓" : "🆕";
      message += `• ${course.title}: ${viewed}/${total} chapters viewed (${percent}%) ${emoji}\n`;
    });

    const notEnrolled = allCourses.filter(c => !enrolledCourses.find(ec => ec.id === c.id));
    if (notEnrolled.length) {
      const suggestion = notEnrolled[Math.floor(Math.random() * notEnrolled.length)];
      message += `\n✨ Want to try something new? Check out **${suggestion.title}**. Type \`enroll(${suggestion.id})\` to join 💡`;
    }

    return message;
  };

  return (
    <Box sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1300 }}>
      {!isOpen && (
        <Fab style={{ backgroundColor: "#ff6600", color: "white" }} onClick={() => setIsOpen(true)} aria-label="chat">
          <ChatIcon />
        </Fab>
      )}

      {isOpen && (
        <Paper
          elevation={6}
          sx={{
            width: 360,
            height: 500,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 3,
            overflow: 'hidden',
            animation: 'fadeIn 0.3s ease',
          }}
        >
          <Box
            sx={{
              background: 'linear-gradient(135deg, #ff6600 0%, rgba(0,0,0,0.7) 100%)',
              color: 'white',
              p: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Avatar
              src="./avatars/deepblessings"
              alt="Bot"
              sx={{
                width: 40,
                height: 40,
                border: '2px solid white',
                boxShadow: '0 0 8px 2px rgba(255, 255, 255, 0.7)',
                animation: 'pulse 2.5s infinite',
              }}
            />
            <Typography variant="subtitle1" sx={{ ml: 2, fontWeight: 'bold', flexGrow: 1 }}>
              {title}
            </Typography>
            <IconButton size="small" sx={{ color: 'white' }} onClick={() => setIsOpen(false)} aria-label="close chat">
              <CloseIcon />
            </IconButton>
            <style>{`
              @keyframes pulse {
                0% { box-shadow: 0 0 8px 2px rgba(255, 255, 255, 0.7); }
                50% { box-shadow: 0 0 12px 4px rgba(255, 255, 255, 1); }
                100% { box-shadow: 0 0 8px 2px rgba(255, 255, 255, 0.7); }
              }
            `}</style>
          </Box>

          <Box
            ref={chatBoxRef}
            sx={{
              flex: 1,
              p: 2,
              overflowY: 'auto',
              backgroundColor: '#f9f9f9',
            }}
          >
            {chat.map((msg, idx) => (
              <Box key={idx} sx={{ textAlign: msg.sender === 'user' ? 'right' : 'left', my: 1 }}>
                <Box
                  sx={{
                    display: 'inline-block',
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    bgcolor: msg.sender === 'user' ? 'primary.main' : 'grey.300',
                    color: msg.sender === 'user' ? 'white' : 'text.primary',
                    maxWidth: '80%',
                    wordBreak: 'break-word',
                    fontStyle: msg.text === 'typing' ? 'normal' : 'inherit',
                  }}
                >
                  {msg.text === 'typing' ? <TypingDots /> : msg.text}
                </Box>
              </Box>
            ))}
          </Box>

          <Divider />

          <Box sx={{ p: 2, display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Ask a question..."
              value={question}
              onChange={e => setQuestion(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') sendMessage();
              }}
              disabled={loading}
              autoFocus
            />
            <Button
              style={{ backgroundColor: "#ff6600", color: "white" }}
              onClick={sendMessage}
              disabled={loading}
            >
              Send
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default ChatWidget;
